import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { motion } from 'framer-motion';
import { CheckCircle, Heart, Info } from 'lucide-react';
import { profileAPI } from '../../api/profile';
import { Card } from '../../components/ui/Card';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import PageHeader from '../../components/ui/PageHeader';

export default function NomineeDetailsPage() {
  const [loading, setLoading]   = useState(true);
  const [isUpdate, setIsUpdate] = useState(false); // true → PUT, false → POST
  const [success, setSuccess]   = useState(false);
  const [apiError, setApiError] = useState('');

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      name:         '',
      age:          '',
      mobile:       '',
      aadhar_card:  '',
    },
  });

  /* API #6 — pre-fill if nominee exists ──────────────────── */
  useEffect(() => {
    profileAPI
      .getProfile()
      .then((res) => {
        const nominee = res.data.data?.nominee;
        if (nominee) {
          setIsUpdate(true);
          reset({
            name:        nominee.name        ?? '',
            age:         nominee.age         ?? '',
            mobile:      nominee.mobile      ?? '',
            aadhar_card: nominee.aadhar_card ?? '',
          });
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [reset]);

  /* API #12 (POST) or #13 (PUT) ──────────────────────────── */
  const onSubmit = async (data) => {
    setApiError('');
    // cast age to integer before sending
    const payload = { ...data, age: parseInt(data.age, 10) };

    try {
      if (isUpdate) {
        await profileAPI.updateNominee(payload);   // PUT /profile/nominee
      } else {
        await profileAPI.storeNominee(payload);    // POST /profile/nominee
        setIsUpdate(true);
      }
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      const errs = err.response?.data?.errors;
      if (errs) {
        const first = Object.values(errs)[0]?.[0];
        setApiError(first || 'Validation failed.');
      } else {
        setApiError(err.response?.data?.message || 'Failed to save. Please try again.');
      }
    }
  };

  return (
    <div>
      <PageHeader
        title="Nominee Details"
        subtitle={isUpdate ? 'Update nominee information' : 'Add a nominee'}
        showBack
      />

      <div className="px-4 py-4 pb-6">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 rounded-full border-2 border-amber-400 border-t-transparent animate-spin" />
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="flex flex-col gap-4"
          >
            {/* Mode indicator */}
            <div className="flex items-center gap-2 px-1">
              <div
                className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: 'linear-gradient(135deg, #d4a017, #f0c040)' }}
              >
                <Heart size={15} color="white" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-800">
                  {isUpdate ? 'Update Nominee' : 'Add Nominee'}
                </p>
                <p className="text-xs text-gray-400">
                  {isUpdate
                    ? 'Your saved details are pre-filled below'
                    : 'No nominee added yet'}
                </p>
              </div>
            </div>

            <Card>
              {/* Success banner */}
              {success && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="mb-4 rounded-xl bg-emerald-50 border border-emerald-100 px-4 py-3 text-sm text-emerald-700 flex items-center gap-2"
                >
                  <CheckCircle size={16} />
                  Nominee details {isUpdate ? 'updated' : 'saved'} successfully!
                </motion.div>
              )}

              {/* Error banner */}
              {apiError && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="mb-4 rounded-xl bg-red-50 border border-red-100 px-4 py-3 text-sm text-red-600"
                >
                  {apiError}
                </motion.div>
              )}

              <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">

                {/* Nominee Name */}
                <Input
                  label="Nominee Name"
                  placeholder="Full name"
                  error={errors.name?.message}
                  {...register('name', {
                    required:  'Nominee name is required',
                    maxLength: { value: 100, message: 'Max 100 characters' },
                  })}
                />

                {/* Age */}
                <Input
                  label="Age"
                  type="number"
                  placeholder="e.g. 28"
                  error={errors.age?.message}
                  {...register('age', {
                    required: 'Age is required',
                    min:      { value: 1,   message: 'Age must be at least 1' },
                    max:      { value: 120, message: 'Age cannot exceed 120' },
                    validate: (v) => Number.isInteger(Number(v)) || 'Age must be a whole number',
                  })}
                />

                {/* Mobile */}
                <Input
                  label="Mobile Number"
                  type="tel"
                  placeholder="10-digit mobile number"
                  error={errors.mobile?.message}
                  {...register('mobile', {
                    required: 'Mobile number is required',
                    pattern:  { value: /^\d{10}$/, message: 'Enter a valid 10-digit number' },
                  })}
                />

                {/* Aadhaar Card */}
                <Input
                  label="Aadhaar Card Number (Optional)"
                  placeholder="e.g. 1234-5678-9012"
                  error={errors.aadhar_card?.message}
                  {...register('aadhar_card', {
                    pattern: {
                      value:   /^\d{4}-\d{4}-\d{4}$/,
                      message: 'Format: 1234-5678-9012',
                    },
                  })}
                />

                <Button type="submit" loading={isSubmitting} fullWidth>
                  {isUpdate ? 'Update Nominee' : 'Save Nominee'}
                </Button>
              </form>
            </Card>

            {/* Aadhaar info note */}
            <div className="flex items-start gap-2 px-1">
              <Info size={13} className="text-amber-500 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-gray-400 leading-relaxed">
                Aadhaar format: four digits, hyphen, four digits, hyphen, four digits
                (e.g. 1234-5678-9012). Nominee details are used for claim processing.
              </p>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
